import type { RequestFn } from '../http';
import type { ContactResponsibility } from './customerCare';
import type { PendingWorkCounters } from './portalTimeBooking';
import { API_BASE } from '../http';
import type { KianUsage, WithKianUsage } from '../types/core';

/**
 * Ob die Arbeit an einem Ticket im Regelbetrieb läuft oder separat beauftragt wurde.
 * Nur `STANDARD` zählt gegen das Kontingent des Kunden. Andere Frage als die
 * Abrechnungsart einer Zeitbuchung (abrechenbar / intern / Kulanz) — die sagt, OB
 * abgerechnet wird, diese WORAUS.
 */
export type TicketBillingScope = 'STANDARD' | 'QUOTE' | 'PROJECT';

/** Regel, nach der ein Statuswechsel mailt oder nicht (backend/src/services/ticketStatusMail.ts). */
export type StatusMailRule =
  | 'FIRST_RESOLUTION'
  | 'NEW_CUSTOMER_CONTACT'
  | 'REWORK'
  | 'ALREADY_RESOLVED_API'
  | 'BOOKING_WINDOW'
  | 'MANUAL';

/** Vorschlag für das Häkchen „Kunde benachrichtigen“ (GET /weclapp/tickets/:id/status-mail). */
export interface StatusMailPreview {
  decision: { notify: boolean; rule: StatusMailRule };
  label: string;
  targetResolved: boolean;
  currentResolved: boolean;
  resolvedAt: string | null;
  lastCustomerContact: { at: string; source: 'MAIL' | 'PORTAL_COMMENT' | 'CALL' | 'WHATSAPP' } | null;
  /** Häkchen anbieten — das Ticket war schon einmal erledigt. */
  offerChoice: boolean;
  /** Vorlagen am Ticket aus — weclapp verschickt auch mit Häkchen nichts (null = unbekannt). */
  templatesDisabled?: boolean | null;
}

export interface TicketBillingScopeView {
  weclappTicketId: string;
  scope: TicketBillingScope;
  reference: string | null;
  note: string | null;
  setByName: string | null;
  updatedAt: string | null;
  /** true, solange nichts hinterlegt ist — dann gilt der Standard, niemand hat entschieden. */
  isDefault: boolean;
}

/**
 * Wie der Kunde für Servicezeit zahlt — Grundlage für den Hinweis im Cockpit.
 * Andere Frage als die Abrechnungsgrundlage am Ticket: Die sagt, ob die Arbeit separat
 * beauftragt wurde, das hier, wie der Vertrag insgesamt gebaut ist.
 */
export type CustomerContractModel = 'UNKNOWN' | 'FLAT_RATE' | 'HOURLY' | 'MIXED';

export interface TicketContractHint {
  model: CustomerContractModel;
  label: string;
  hint: string;
  color: 'gray' | 'teal' | 'blue' | 'violet';
  includedHoursPerMonth: number | null;
}

export const TICKET_BILLING_SCOPE_LABELS: Record<TicketBillingScope, string> = {
  STANDARD: 'Regelbetrieb',
  QUOTE: 'Angebot',
  PROJECT: 'Projekt',
};

/** Ticket-Typ der KIAN-Aufnahme (ITIL: Störung / Auftrag / Wartung-proaktiv / Beratung-Termin). */
export type WeclappTicketIntakeType = 'incident' | 'request' | 'maintenance' | 'consulting';

/** KIAN-Entwurf aus der Ticket-Aufnahme — reiner Formular-Prefill, nichts wird angelegt. */
export interface WeclappTicketIntakeDraft {
  ticketType: WeclappTicketIntakeType;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent' | null;
  priorityId: string | null;
  categoryId: string | null;
  categoryName: string | null;
  /** Bei der Aufnahme nicht erfragte Kern-Infos — als Hinweis im Formular. */
  missingInfo: string[];
}

export interface WeclappContactDetail {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  /**
   * Haupt-Ansprechpartner. Quelle ist die portal-interne Zuständigkeit; Weclapps
   * `party.primaryContactId` gilt nur, solange intern nichts gepflegt ist.
   */
  isPrimary?: boolean;
  /** Portal-interne Zuständigkeiten (Haupt-AP + Themen wie Angebote/Rechnungen). */
  responsibilities?: ContactResponsibility[];
}

/** Wie der Melder eines Tickets gefunden wurde — bestimmt den Hinweistext im Cockpit. */
export type ReporterMatch = 'ticketContact' | 'senderEmail' | 'name';

/** Rückfrage-Kontakte eines Tickets: Melder, Haupt-Ansprechpartner, alle Kontakte. */
export interface TicketFollowUpContacts {
  /** null = Melder nicht eindeutig auflösbar; dann NIE stillschweigend ersetzen. */
  reporter: (WeclappContactDetail & { matchedBy: ReporterMatch }) | null;
  reporterEmail: string | null;
  reporterName: string | null;
  /** Haupt-Ansprechpartner (portal-intern gepflegt) — es können mehrere sein, leer = keiner. */
  primaryContacts: WeclappContactDetail[];
  contacts: WeclappContactDetail[];
  /** Rufnummern des Melders (Weclapp + portal-intern) für den Chat-Abgleich. */
  reporterPhones: string[];
}

export interface WeclappContactInput {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
}

export interface WeclappSenderEmail {
  email: string;
  isBounce: boolean;
  isFreemail: boolean;
  source: 'from' | 'to' | 'body';
}

/** Zwilling im Massen-Schließen: identischer Betreff + Ausgangszustand, inkl. Quelle & Kunden-Vorschlag. */
export interface SameStateTicket {
  id: string;
  ticketNumber: string | null;
  subject: string | null;
  status: string | null;
  partyName: string | null;
  createdDate: string | null;
  senderEmail: string | null;
  toRecipients: string[];
  ccRecipients: string[];
  /** Erste ~400 Zeichen des Ticket-Inhalts (HTML entfernt) — Schnell-Durchsicht per Hover. */
  descriptionPreview: string | null;
  /** Absender + Empfänger identisch zum Ausgangsticket → Kunde wird identisch sein. */
  identicalSource: boolean;
  /** Kunden-Vorschlag für Tickets ohne Kunde: Ref-Kunde (IDENTICAL) oder KIAN-Kandidat. */
  suggestion: {
    customerId: string;
    customerName: string;
    customerNumber: string | null;
    source: 'IDENTICAL' | 'KIAN';
    matchMethod?: string;
  } | null;
}

export interface WeclappCustomerCandidate {
  customerId: string;
  customerName: string;
  customerNumber: string | null;
  weclappPartyId: string | null;
  matchMethod: 'EMAIL_EXACT' | 'EMAIL_DOMAIN' | 'NAME_DOMAIN' | 'HISTORY';
  matchedEmail: string;
  matchedContactEmail: string | null;
}

/** Zuordnungsregel: Absender und/oder Empfänger und/oder Betreff-Teil → Kunde. */
export interface TicketCustomerRule {
  id: string;
  enabled: boolean;
  senderPattern: string | null;
  recipientPattern: string | null;
  subjectContains: string | null;
  customerId: string;
  customerName: string;
  createdByName: string | null;
  matchCount: number;
  lastMatchedAt: string | null;
  createdAt: string;
}

export type TicketMatchStatus =
  | 'AUTO_ASSIGNED'
  | 'SUGGESTED'
  | 'NEEDS_REVIEW'
  | 'MANUAL_ASSIGNED'
  | 'DISMISSED';

export interface WeclappCustomerMatch {
  status: TicketMatchStatus;
  assignedBy: 'KIAN' | 'TECHNICIAN' | null;
  confidence: number | null;
  matchMethod: string | null;
  senderEmail: string | null;
  suggestedCustomerId: string | null;
  suggestedCustomerName: string | null;
  assignedCustomerId: string | null;
  assignedCustomerName: string | null;
  kianReasoning: string | null;
  updatedAt: string;
}

export interface KianMatchOutcome {
  status: 'AUTO_ASSIGNED' | 'SUGGESTED' | 'NEEDS_REVIEW';
  customerId: string | null;
  customerName: string | null;
  confidence: number | null;
  reasoning: string | null;
  senderEmail: string | null;
}

import type {
  DispatchBoardResponse,
  KanbanResponse,
  KanbanTechnician,
  SyncStatistics,
  SyncStatus,
  WeclappCustomer,
  WeclappCustomerMapping,
  WeclappCustomerMappingFullResponse,
  WeclappDispatchFilters,
  WeclappDocumentRef,
  WeclappKanbanFilters,
  WeclappMetadata,
  WeclappPendingTimeBooking,
  WeclappPerformanceRecord,
  WeclappPriority,
  WeclappStatus,
  WeclappTicket,
  WeclappTicketComment,
  WeclappTicketFilters,
  WeclappTicketHistoryEntry,
  WeclappTicketMail,
  WeclappTicketCommentRecipients,
  WeclappTicketListResponse,
  WeclappSemanticSearchResponse,
} from '../types';
import type { CWBoard, CWReference } from '../types/weclapp';

/** KIAN-Antwortvorschlag (backend/src/services/kianReplyProposals.ts, `ReplyProposalView`). */
export interface KianReplyProposal {
  id: string;
  ticketId: string;
  /** Text, der beim Öffentlich-Stellen an den Kunden geht (ohne Hinweiszeile) */
  body: string;
  /** PROPOSED | SENDING | SENT | UNKNOWN | DISCARDED */
  status: 'PROPOSED' | 'SENDING' | 'SENT' | 'UNKNOWN' | 'DISCARDED' | 'NOT_SAVED';
  noteStatus: string | null;
  proposedBy: string | null;
  createdAt: string;
  sentAt: string | null;
  sentByName: string | null;
  sentPending: boolean;
  discardedAt: string | null;
  lastError: string | null;
}

/** Anfang der Hinweiszeile im internen Kommentar — gleich `REPLY_PROPOSAL_MARKER` im Backend. */
export const KIAN_REPLY_PROPOSAL_MARKER = '[KIAN-Antwortvorschlag';

/** Tagesstand der KIAN-Schreibhilfen je Techniker (Kontingent + Kosten). */
export interface KianAssistUsage {
  used: number;
  limit: number;
  remaining: number;
  costEurToday: number;
  resetAt: string;
}

export function createWeclappApi(request: RequestFn) {
  return {
    async getWeclappStatus() {
      return request<{
        configured: boolean;
        connected?: boolean;
        message: string;
        ticketCount?: number;
      }>('/weclapp/status');
    },

    async getWeclappSyncStats() {
      return request<SyncStatistics>('/weclapp/sync-stats');
    },

    /**
     * `signal`: react-query reicht sein AbortSignal durch — tippt jemand weiter, bricht der
     * Browser die vorige Suchanfrage ab, statt dass sich veraltete Antworten stapeln.
     */
    async getWeclappTickets(filters: WeclappTicketFilters = {}, options: { signal?: AbortSignal } = {}) {
      const params = new URLSearchParams();
      if (filters.customerId) params.set('customerId', filters.customerId);
      if (filters.statusId) params.set('statusId', filters.statusId);
      if (filters.statusIds && filters.statusIds.length > 0) {
        params.set('statusIds', filters.statusIds.join(','));
      }
      if (filters.priorityId) params.set('priorityId', filters.priorityId);
      if (filters.search) params.set('search', filters.search);
      if (filters.createdDateFrom) params.set('createdDateFrom', filters.createdDateFrom);
      if (filters.createdDateTo) params.set('createdDateTo', filters.createdDateTo);
      if (filters.assignedTo) params.set('assignedTo', filters.assignedTo);
      if (filters.assignment && filters.assignment !== 'all') {
        params.set('assignment', filters.assignment);
      }
      if (filters.openOnly) params.set('openOnly', 'true');
      if (filters.closedOnly) params.set('closedOnly', 'true');
      if (filters.customerMissing) params.set('customerMissing', 'true');
      if (filters.modifiedDateFrom) params.set('modifiedDateFrom', filters.modifiedDateFrom);
      if (filters.untouchedOnly) params.set('untouchedOnly', 'true');
      if (filters.overdue) params.set('overdue', filters.overdue);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
      if (filters.paging) params.set('paging', filters.paging);
      if (filters.after) params.set('after', filters.after);

      const query = params.toString() ? `?${params.toString()}` : '';
      return request<WeclappTicketListResponse>(`/weclapp/tickets${query}`, { signal: options.signal });
    },

    /**
     * Bedeutungssuche („Ähnliche Tickets") über den Hivemind-Index. Kostet je Anfrage einen
     * Embedding-Aufruf — nur auf Enter/Klick aufrufen, nie bei jedem Tastendruck.
     */
    async searchWeclappTicketsByMeaning(
      params: {
        q: string;
        statusIds?: string[];
        openOnly?: boolean;
        closedOnly?: boolean;
        priorityId?: string;
        assignedTo?: string;
        assignment?: 'all' | 'assigned' | 'unassigned';
        customerMissing?: boolean;
      },
      options: { signal?: AbortSignal } = {},
    ) {
      const search = new URLSearchParams();
      search.set('q', params.q);
      if (params.statusIds?.length) search.set('statusIds', params.statusIds.join(','));
      if (params.openOnly) search.set('openOnly', 'true');
      if (params.closedOnly) search.set('closedOnly', 'true');
      if (params.priorityId) search.set('priorityId', params.priorityId);
      if (params.assignedTo) search.set('assignedTo', params.assignedTo);
      if (params.assignment && params.assignment !== 'all') search.set('assignment', params.assignment);
      if (params.customerMissing) search.set('customerMissing', 'true');
      return request<WeclappSemanticSearchResponse>(`/weclapp/tickets/semantic?${search.toString()}`, {
        signal: options.signal,
      });
    },

    async getWeclappTicket(ticketId: string) {
      return request<{ ticket: WeclappTicket; syncStatus: SyncStatus | null }>(
        `/weclapp/tickets/${ticketId}`
      );
    },

    async getWeclappTicketLive(ticketId: string) {
      return request<{ ticket: WeclappTicket; syncStatus: SyncStatus | null; changed: boolean }>(
        `/weclapp/tickets/${ticketId}/live`
      );
    },

    async createWeclappTicket(body: {
      partyId: string;
      subject: string;
      description?: string;
      deadline?: string;
      ticketStatusId?: string;
      ticketPriorityId?: string;
      ticketCategoryId?: string;
      contactId?: string;
      initialNote?: string;
      initialNoteInternal?: boolean;
    }) {
      return request<{
        id: string;
        ticketNumber: string;
        contactIdDropped?: boolean;
        /** Status verlangte einen Bearbeiter → Ticket wurde dem Anleger zugewiesen */
        assignedToCreator?: boolean;
        /** Kein Weclapp-User für den Anleger → ohne Status angelegt (Weclapp-Default) */
        statusFallback?: boolean;
        /**
         * Nur im ServiceHub angelegt — weclapp hat nicht rechtzeitig angenommen, der Nachsync reicht
         * das Ticket nach. `id` ist dann die interne ID (`local-…`), `ticketNumber` die eigene
         * Nummer (S26-…). Ältere Server liefern das Feld nicht.
         */
        local?: boolean;
        localNumber?: string;
        pendingReason?: string;
      }>(
        '/weclapp/tickets',
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
    },

    async uploadWeclappAttachment(
      ticketId: string,
      file: { base64: string; filename?: string; mimeType?: string }
    ) {
      // `documentId` wird für eingebettete Bilder im Kommentar-Editor gebraucht. `pending`: Die Datei
      // liegt am Ticket im ServiceHub und geht nach weclapp, sobald es das Ticket hat bzw. antwortet.
      return request<{ success: boolean; documentId: string | null; pending?: boolean; pendingReason?: string }>(`/weclapp/tickets/${ticketId}/attachments`, {
        method: 'POST',
        body: JSON.stringify(file),
      });
    },

    async getWeclappMetadata() {
      return request<WeclappMetadata>('/weclapp/metadata');
    },

    async getWeclappStatusesList() {
      return request<{ statuses: WeclappStatus[] }>('/weclapp/statuses');
    },

    async getWeclappPriorities() {
      return request<{ priorities: WeclappPriority[] }>('/weclapp/priorities');
    },

    async getWeclappKanbanTickets(filters: WeclappKanbanFilters = {}) {
      const params = new URLSearchParams();
      if (filters.statusIds?.length) params.set('statusIds', filters.statusIds.join(','));
      if (filters.search) params.set('search', filters.search);
      if (filters.includeClosed !== undefined) {
        params.set('includeClosed', String(filters.includeClosed));
      }
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<KanbanResponse>(`/weclapp/kanban${query}`);
    },

    async getWeclappKanbanTechnicians() {
      return request<{ technicians: KanbanTechnician[] }>('/weclapp/kanban/technicians');
    },

    async getWeclappDispatchBoard(filters: WeclappDispatchFilters = {}) {
      const params = new URLSearchParams();
      if (filters.statusIds?.length) params.set('statusIds', filters.statusIds.join(','));
      if (filters.search) params.set('search', filters.search);
      if (filters.includeClosed !== undefined) {
        params.set('includeClosed', String(filters.includeClosed));
      }
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<DispatchBoardResponse>(`/weclapp/dispatch-board${query}`);
    },

    async searchWeclappCustomers(search: string) {
      return request<{ customers: WeclappCustomer[] }>(
        `/weclapp/customers/search?q=${encodeURIComponent(search)}`
      );
    },

    /** KIAN-Adressvorschläge für Kunden ohne hinterlegte Adresse (Ticket-Text + OSM). */
    async suggestCustomerAddresses(body: { customerName: string; weclappTicketId?: string | null }) {
      return request<{
        suggestions: Array<{
          label: string;
          street: string | null;
          postalCode: string | null;
          city: string | null;
          source: 'ticket_text' | 'web' | 'osm';
          hint: string;
          lat: number;
          lon: number;
        }>;
        sourcesTried: string[];
      }>('/weclapp/customers/address-suggest', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async getAllWeclappCustomers() {
      return request<{ customers: { id: string; name: string; customerNumber?: string }[] }>(
        '/weclapp/customers/all'
      );
    },

    async getWeclappCustomerMappings() {
      return request<{ mappings: WeclappCustomerMapping[] }>('/weclapp/customer-mappings');
    },

    async createWeclappCustomerMapping(data: {
      weclappCustomerId: string;
      weclappCustomerName?: string;
      cwCompanyId: number;
      cwCompanyName?: string;
    }) {
      return request<{ mapping: WeclappCustomerMapping }>('/weclapp/customer-mappings', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async deleteWeclappCustomerMapping(id: string) {
      return request<{ success: boolean }>(`/weclapp/customer-mappings/${id}`, {
        method: 'DELETE',
      });
    },

    async getWeclappCustomerMappingsFull() {
      return request<WeclappCustomerMappingFullResponse>('/weclapp/customer-mappings/full');
    },

    async applyWeclappAutoMappings(
      suggestions: {
        weclappId: string;
        weclappName: string;
        cwCompanyId: number;
        cwCompanyName: string;
      }[]
    ) {
      return request<{ success: boolean; created: number }>('/weclapp/customer-mappings/auto-apply', {
        method: 'POST',
        body: JSON.stringify({ suggestions }),
      });
    },

    async getWeclappCwBoards() {
      return request<{ boards: CWBoard[] }>('/weclapp/cw-boards');
    },

    async getWeclappCwStatuses(boardId: number) {
      return request<{ statuses: CWReference[] }>(`/weclapp/cw-statuses?boardId=${boardId}`);
    },

    async getWeclappCwPriorities() {
      return request<{ priorities: CWReference[] }>('/weclapp/cw-priorities');
    },

    async clearWeclappCache() {
      return request<{ success: boolean; message: string }>('/weclapp/cache/clear', {
        method: 'POST',
      });
    },

    // ── Techniker-Cockpit ─────────────────────────────────────────────────────

    async getMyWeclappTickets() {
      return request<{
        tickets: WeclappTicket[];
        unassignedRecent?: WeclappTicket[];
        missingWeclappUserId?: boolean;
        /** Globale Offen-Stats (alle nicht geschlossenen Tickets) für das Nav-Badge. */
        openStats?: {
          newToday: number;
          unassigned: number;
          total: number;
        } | null;
        /** Datenquelle: 'db' = PostgreSQL-Spiegel, 'memory' = In-Memory-Snapshot/Live-API */
        source?: 'db' | 'memory';
      }>('/weclapp/my-tickets');
    },

    async getWeclappMirrorFreshness() {
      return request<{
        readsEnabled: boolean;
        totalTickets: number;
        /** Letzte eingegangene Änderung (jüngste `syncedAt`) — steht still, wenn nichts passiert. */
        lastSyncedAt: string | null;
        lastTicketCreatedAt: string | null;
        /** Ende des letzten Ticket-Sync-Laufs, auch eines Leerlaufs. */
        lastRunAt?: string | null;
        /** Fehler des letzten Laufs, wenn er gar nichts spiegeln konnte. */
        lastRunError?: string | null;
        /** Ende des letzten Laufs, der durchgekommen ist. */
        lastOkRunAt?: string | null;
      }>('/weclapp/mirror-freshness');
    },

    /**
     * Wartet noch etwas von mir bei Weclapp? Nur die eigenen Vorgänge.
     *
     * `waiting` löst sich von allein, sobald Weclapp antwortet. `stuck` heißt: kein Job
     * versucht es erneut — da muss jemand ran.
     */
    async getWeclappPendingWork() {
      return request<PendingWorkCounters>('/weclapp/pending-work');
    },

    async getWeclappTicketMails(ticketId: string) {
      return request<{ emails: WeclappTicketMail[] }>(`/weclapp/tickets/${ticketId}/emails`);
    },

    /**
     * Personen am Ticket (Ansprechpartner, Absender, An/CC aus dem Verlauf) —
     * Vorschau, bevor ein externer Kommentar rausgeht.
     */
    async getWeclappTicketCommentRecipients(ticketId: string) {
      return request<WeclappTicketCommentRecipients>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/comment-recipients`
      );
    },

    /** KIAN-Antwortvorschläge am Ticket (interne Kommentare, die ein Mensch öffentlich stellt). */
    async getKianReplyProposals(ticketId: string) {
      return request<{ proposals: KianReplyProposal[] }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/reply-proposals`
      );
    },

    /**
     * Antwortvorschlag öffentlich stellen: legt einen NEUEN öffentlichen Kommentar ohne Hinweiszeile an
     * (weclapp schickt dem Kunden die Kommentar-Mail). Genau einmal — ein zweiter Klick ist 409.
     * `confirmDuplicateRisk` nur nach einem Abbruch ohne Antwort (Code `DUPLICATE_RISK`).
     */
    async publishKianReplyProposal(ticketId: string, proposalId: string, opts: { confirmDuplicateRisk?: boolean } = {}) {
      return request<{ status: 'SENT' | 'UNKNOWN'; pending: boolean; proposal: KianReplyProposal }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/reply-proposals/${encodeURIComponent(proposalId)}/publish`,
        { method: 'POST', body: JSON.stringify(opts.confirmDuplicateRisk ? { confirmDuplicateRisk: true } : {}) },
      );
    },

    /** Antwortvorschlag verwerfen — nur Markierung, der interne Kommentar bleibt im Ticket. */
    async discardKianReplyProposal(ticketId: string, proposalId: string) {
      return request<{ proposal: KianReplyProposal }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/reply-proposals/${encodeURIComponent(proposalId)}/discard`,
        { method: 'POST', body: JSON.stringify({}) },
      );
    },

    async getWeclappTicketComments(ticketId: string) {
      return request<{ comments: WeclappTicketComment[] }>(`/weclapp/tickets/${ticketId}/comments`);
    },

    async addWeclappTicketComment(
      ticketId: string,
      data: {
        /** Klartext-Fassung — bleibt Pflicht als Fallback für Ansichten ohne HTML. */
        comment: string;
        /** Optionale formatierte Fassung (Weclapp-Feld `htmlComment`). */
        htmlComment?: string;
        internal: boolean;
        mentionedUserIds?: string[];
        /** Idempotenz-ID — eine pro ausgefülltem Formular, schützt vor Doppel-Kommentar. */
        externalReference?: string;
      },
    ) {
      return request<{
        success: true;
        duplicate?: boolean;
        /**
         * Weclapp war nicht erreichbar: Der Kommentar liegt im Portal und wird nachgesendet.
         * Gespeichert ist er — angekommen ist er noch nicht, und niemand liest ihn dort.
         */
        pending?: boolean;
      }>(`/weclapp/tickets/${ticketId}/comments`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * KIAN formuliert einen Kommentar-Entwurf. Postet nichts — die Antwort landet im
     * Editor und wird erst nach Freigabe des Technikers gesendet.
     */
    async draftWeclappTicketComment(
      ticketId: string,
      data: {
        draft?: string;
        draftHtml?: string;
        internal: boolean;
        mode?: 'STANDARD' | 'DEEP';
        /** TIME_ENTRY: kundentauglicher Leistungsnachweis-Text (Servicebericht) statt Kommentar. */
        usage?: 'COMMENT' | 'TIME_ENTRY' | 'HANDOVER';
        /**
         * Wie der Rohtext zu behandeln ist: POLISH = nur in Form bringen (Inhalt unangetastet),
         * INSTRUCT = als Anweisung ausformulieren (Ticketkontext als Material),
         * AUTO (Default) = kurze Eingaben als Anweisung, längere polieren.
         */
        intent?: 'AUTO' | 'POLISH' | 'INSTRUCT';
      },
    ) {
      return request<{ text: string; html?: string; refined: boolean; mode: 'STANDARD' | 'DEEP' }>(
        `/weclapp/tickets/${ticketId}/comment-draft`,
        { method: 'POST', body: JSON.stringify(data) },
      );
    },

    /**
     * Interner Anstupser an den Bearbeiter: interner Kommentar + Glocke/Push/Mail. `note` sagt,
     * wo der Kommentar steht — in weclapp (SYNCED), erst im Portal (PENDING, wird nachgetragen)
     * oder mit unklarem Ausgang (FAILED). `CUSTOM` = nur der eigene Text in `note` (Pflicht).
     */
    async nudgeWeclappTicket(
      ticketId: string,
      data: { kind: 'REMINDER' | 'STATUS' | 'URGENT' | 'CUSTOM'; note?: string },
    ) {
      return request<{ success: true; notified: string; note?: 'SYNCED' | 'PENDING' | 'FAILED' }>(`/weclapp/tickets/${ticketId}/nudge`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /** Offene Tickets mit identischem Betreff + gleichem Status/Zuweisung (Massen-Schließen). */
    async getWeclappSameStateTickets(ticketId: string) {
      return request<{
        /** Quelle des Ausgangstickets — Vergleichsbasis für „identische Flut". */
        refSource?: {
          senderEmail: string | null;
          toRecipients: string[];
          ccRecipients: string[];
          customerName: string | null;
        };
        tickets: SameStateTicket[];
      }>(`/weclapp/tickets/${ticketId}/same-state`);
    },

    /** Geteilte Ticket-Referenz (Nummer oder ID) → interne Weclapp-ID. Für Deeplinks. */
    async resolveWeclappTicketRef(ref: string) {
      return request<{ id: string; ticketNumber?: string | null; subject?: string | null }>(
        `/weclapp/tickets/resolve/${encodeURIComponent(ref)}`,
      );
    },

    /**
     * Aus welchem Topf die Arbeit am Ticket bezahlt wird. Antwortet immer — ist nichts
     * hinterlegt, kommt `FLAT` mit `isDefault: true` zurück.
     */
    /**
     * Alle Tickets, die NICHT im Regelbetrieb laufen — als Karte für Listen und Boards.
     * Nur die Ausnahmen, deshalb winzig und einmal für die ganze Ansicht zu holen.
     */
    async getTicketBillingScopes() {
      return request<{
        scopes: Record<string, { scope: TicketBillingScope; reference: string | null }>;
      }>('/weclapp/ticket-billing-scopes');
    },

    async getTicketBillingScope(ticketId: string) {
      return request<{
        billingScope: TicketBillingScopeView;
        /** null = Ticket ohne Kundenzuordnung oder Kunde ohne gepflegtes Vertragsmodell. */
        contract: TicketContractHint | null;
      }>(`/weclapp/tickets/${ticketId}/billing-scope`);
    },

    async setTicketBillingScope(
      ticketId: string,
      data: { scope: TicketBillingScope; reference?: string | null; note?: string | null },
    ) {
      return request<{ billingScope: TicketBillingScopeView }>(
        `/weclapp/tickets/${ticketId}/billing-scope`,
        { method: 'PUT', body: JSON.stringify(data) },
      );
    },

    /** Leistungsarten für Zeitbuchungen — Standard zuerst; leer, wenn nichts konfiguriert ist. */
    async getWeclappBookingArticles() {
      return request<{
        defaultArticleId: string | null;
        options: Array<{ id: string; name: string; articleNumber: string; invoicingType: string | null; isDefault: boolean }>;
      }>('/weclapp/booking-articles');
    },

    async getWeclappTicketTimeEntries(ticketId: string) {
      return request<{ timeEntries: WeclappPerformanceRecord[]; pendingTimeBookings?: WeclappPendingTimeBooking[] }>(
        `/weclapp/tickets/${ticketId}/time-entries`,
      );
    },

    /** Eigene Weclapp-Zeitbuchungen aus dem Spiegel — inkl. Buchungen ohne Ticketbezug. */
    async getMyWeclappTimeEntries(params: { scope?: 'all' | 'ticketless'; from?: string; to?: string } = {}) {
      const search = new URLSearchParams();
      if (params.scope) search.set('scope', params.scope);
      if (params.from) search.set('from', params.from);
      if (params.to) search.set('to', params.to);
      const qs = search.toString();
      return request<{
        weclappUserMissing: boolean;
        entries: Array<{
          id: string;
          ticketId: string | null;
          taskId: string | null;
          ticketNumber: string | null;
          ticketSubject: string | null;
          comment: string | null;
          hours: number | null;
          startDate: string | null;
        }>;
      }>(`/weclapp/my-time-entries${qs ? `?${qs}` : ''}`);
    },

    async getWeclappTicketHistory(ticketId: string) {
      return request<{ history: WeclappTicketHistoryEntry[] }>(`/weclapp/tickets/${ticketId}/history`);
    },

    /** Gebündeltes Cockpit (Kopf + Kommentare + Zeiten + Dokumente + Mails) in einem Call. */
    async getWeclappTicketCockpit(ticketId: string) {
      return request<{
        source: 'mirror' | 'live';
        ticket: WeclappTicket;
        comments: WeclappTicketComment[];
        /**
         * Der Kommentarstand konnte nicht bei weclapp aufgefrischt werden — angezeigt wird,
         * was zuletzt bekannt war. Gehört sichtbar gemacht: Ein Verlauf ohne die Antwort, die
         * ein Kunde vor zwei Minuten geschrieben hat, sieht aus wie ein Verlauf ohne Antwort.
         */
        commentsStale?: boolean;
        timeEntries: WeclappPerformanceRecord[];
        pendingTimeBookings?: WeclappPendingTimeBooking[];
        documents: (WeclappDocumentRef & { source?: string })[];
        /** Gesetzt, wenn die Dokumentliste NICHT geladen werden konnte — „keine" wäre gelogen. */
        documentsError?: string | null;
        mails: WeclappTicketMail[];
        history: unknown[];
        syncedAt?: string;
      }>(`/weclapp/tickets/${ticketId}/cockpit`);
    },

    /** „Jetzt nachbuchen" an einer wartenden Buchung (Warteliste) — derselbe Weg wie der Retry-Job. */
    async retryWeclappTimeBooking(ticketId: string, bookingId: string) {
      return request<{ status: 'SYNCED' | 'PENDING' | 'ERROR'; warnings: string[]; lastError: string | null }>(
        `/weclapp/tickets/${ticketId}/time-bookings/${bookingId}/retry`,
        { method: 'POST' },
      );
    },

    async addWeclappTimeEntry(
      ticketId: string,
      data: {
        hours: number;
        comment?: string;
        startDate?: number;
        /**
         * Ende der Buchung (ms) — statt `startDate`: Der Server legt Dauer samt Aufrundung
         * auf den Viertelstunden-Takt VOR dieses Ende. Gewinnt, wenn beides kommt.
         */
        endDate?: number;
        /** Buchung für einen anderen Techniker (weclapp-User-ID). Fehlt: der Angemeldete. */
        technicianWeclappUserId?: string;
        /** Idempotenz-ID — eine pro ausgefülltem Formular, schützt vor Doppel-Buchung. */
        externalReference?: string;
        /** Denselben Text zusätzlich als internen/öffentlichen Ticketkommentar posten. */
        alsoComment?: { internal?: boolean; public?: boolean };
        /** Leistungsart (Weclapp-Artikel-ID aus den konfigurierten Optionen); fehlt: Standard. */
        articleId?: string;
      },
    ) {
      // `warning`: Weclapp hat auf den API-Benutzer statt den Techniker gebucht.
      // `pending`: Weclapp hat abgelehnt — Buchung ist lokal geparkt und wird
      // automatisch nachgebucht (Warteliste, Admin-Übersicht unter /admin/sync).
      // `bookedHours`: auf Viertelstunde aufgerundet (Weclapp), `actualHours`: minutengenau erfasst.
      // `proposed` (v1.404.0, HTTP 202): Techniker für einen Kollegen — nichts gebucht, ein
      // Zeitvorschlag wartet auf dessen Freigabe.
      return request<{
        success: true;
        warning?: string;
        duplicate?: boolean;
        pending?: boolean;
        message?: string;
        actualHours?: number;
        bookedHours?: number;
        proposed?: boolean;
        proposal?: { id: string; recipientName: string };
      }>(
        `/weclapp/tickets/${ticketId}/time-entries`,
        { method: 'POST', body: JSON.stringify(data) },
      );
    },

    async updateWeclappTicket(
      ticketId: string,
      data: {
        subject?: string;
        description?: string;
        statusId?: string;
        priorityId?: string;
        categoryId?: string;
        // string = Weclapp-User-ID; null = Verantwortlichen entfernen (Pool)
        responsibleUserId?: string | null;
        /** Pflichtbegründung bei Pool-Rückgabe (`responsibleUserId: null`), min. 10 Zeichen. */
        releaseReason?: string;
        /**
         * Pflichtbegründung, wenn ein TEAMLEAD einer ANDEREN Person zuweist (min. 10 Zeichen).
         * Techniker dürfen nicht an andere zuweisen — dafür die Übergabe nutzen.
         */
        assignReason?: string;
        /**
         * Kennzeichnet, welche der mitgeschickten Texte aus der KIAN-Optimierung
         * stammen. Der Server schreibt das in den Bearbeitungshinweis und die
         * interne Original-Notiz — Transparenz, keine Speicherlogik.
         */
        kianOptimized?: { subject?: boolean; description?: boolean };
        /**
         * Häkchen „Kunde benachrichtigen“ beim (erneuten) Lösen — Statusmails, Regel 5. Fehlt es,
         * entscheidet der Server nach den Regeln (erstes Lösen mailt, Korrektur ohne neuen
         * Kundenkontakt nicht). Weicht es vom Vorschlag ab, steht das im Protokoll.
         */
        notifyCustomer?: boolean;
      }
    ) {
      return request<{
        success: true;
        /** Wie der Statuswechsel mit der Kundenmail umgegangen ist (nur bei Statuswechseln, die etwas entschieden haben). */
        statusMail?: { notify: boolean; rule: StatusMailRule; templatesDisabled?: true };
        /**
         * Weclapp war nicht erreichbar (Ausfall oder Sperre). Die Änderung ist im Portal
         * gespeichert und sofort sichtbar; ein Job trägt sie nach. Wer das nicht anzeigt,
         * lässt den Techniker glauben, der Stand stehe schon in Weclapp.
         */
        pending?: boolean;
        message?: string;
      }>(`/weclapp/tickets/${ticketId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /** Vorschlag für das Häkchen „Kunde benachrichtigen“ beim Statuswechsel (Statusmails, Regel 5). */
    async getWeclappStatusMailPreview(ticketId: string, statusId: string) {
      return request<StatusMailPreview>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/status-mail?statusId=${encodeURIComponent(statusId)}`,
      );
    },

    /**
     * KIAN-Ticket-Aufnahme: freie Beschreibung → Typ, Betreff, strukturierte
     * Beschreibung, Kategorie-/Prio-Vorschlag. Ticketlos — reiner Formular-Entwurf.
     */
    async draftWeclappTicketIntake(data: {
      description: string;
      customerName?: string;
      forcedType?: WeclappTicketIntakeType;
    }) {
      return request<WithKianUsage<WeclappTicketIntakeDraft>>('/weclapp/ticket-intake-draft', {
        method: 'POST',
        kianUsageInline: true,
        body: JSON.stringify(data),
      });
    },

    /**
     * Tagesstand der KIAN-Schreibhilfen (ITIL-Optimierung, Ticket-Aufnahme,
     * Kommentar-Entwurf) für den angemeldeten Techniker.
     */
    async getKianAssistUsage() {
      return request<KianAssistUsage>('/weclapp/kian-assist-usage');
    },

    /** KIAN-Optimierung für Betreff/Beschreibung im Editiermodus — ändert nichts am Ticket. */
    async optimizeWeclappTicketText(
      ticketId: string,
      data: { field: 'subject' | 'description'; subject?: string; descriptionHtml?: string },
    ) {
      return request<{
        text: string;
        html?: string;
        /** Geschätzte Modellkosten dieses Aufrufs. */
        costEur?: number;
        /** Modell, Tokens, Kosten (hängt der Server an jede KIAN-Antwort) */
        kianUsage?: KianUsage;
        /** Tagesstand der KIAN-Schreibhilfen nach diesem Aufruf. */
        quota?: KianAssistUsage;
        /** Was KIAN für das ITIL-Format nicht aus dem Material holen konnte. */
        openPoints?: string[];
        urgency?: 'low' | 'medium' | 'high' | 'urgent' | null;
        /** Vorschlag, wie der Techniker die offenen Punkte klärt — er entscheidet. */
        suggestedChannel?: 'call' | 'whatsapp' | 'comment' | null;
        channelReason?: string | null;
      }>(
        `/weclapp/tickets/${ticketId}/optimize-text`,
        { method: 'POST', body: JSON.stringify(data), kianUsageInline: true },
      );
    },

    async getWeclappAssignableUsers() {
      return request<{ users: { id: string; name: string }[] }>('/weclapp/users');
    },

    // ── Kundenzuordnung (KUNDE FEHLT) ─────────────────────────────────────────

    async getWeclappTicketSenderCandidates(ticketId: string) {
      return request<{
        senderEmails: WeclappSenderEmail[];
        primaryEmail: string | null;
        candidates: WeclappCustomerCandidate[];
        /** Empfänger (An + CC) aller Ticket-Mails — die Mail ging nicht zwingend an support@. */
        recipients: string[];
      }>(`/weclapp/tickets/${encodeURIComponent(ticketId)}/sender-candidates`);
    },

    // ── Zuordnungsregeln (Absender/Empfänger/Betreff → Kunde) ─────────────────

    async getTicketCustomerRules() {
      return request<{ rules: TicketCustomerRule[] }>('/ticket-customer-rules');
    },

    async createTicketCustomerRule(data: {
      senderPattern?: string;
      recipientPattern?: string;
      subjectContains?: string;
      customerId: string;
      applyToExisting?: boolean;
    }) {
      return request<{ rule: { id: string }; backfillStarted: boolean }>('/ticket-customer-rules', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateTicketCustomerRule(id: string, data: { enabled: boolean }) {
      return request<{ success: true }>(`/ticket-customer-rules/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async deleteTicketCustomerRule(id: string) {
      return request<{ success: true }>(`/ticket-customer-rules/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    },

    async assignWeclappTicketCustomer(
      ticketId: string,
      data: { customerId: string; senderEmail?: string; contactName?: string; createContact?: boolean }
    ) {
      return request<{
        success: true;
        weclappPartyId: string;
        contactId: string | null;
        customerName: string;
      }>(`/weclapp/tickets/${encodeURIComponent(ticketId)}/assign-customer`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getWeclappTicketCustomerMatch(ticketId: string) {
      return request<{ match: WeclappCustomerMatch | null }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/customer-match`
      );
    },

    async runWeclappTicketCustomerMatch(ticketId: string) {
      return request<{ outcome: KianMatchOutcome }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/customer-match/run`,
        { method: 'POST' }
      );
    },

    async confirmWeclappTicketCustomerMatch(ticketId: string, data: { createContact?: boolean } = {}) {
      return request<{
        success: true;
        weclappPartyId: string;
        contactId: string | null;
        customerName: string;
      }>(`/weclapp/tickets/${encodeURIComponent(ticketId)}/customer-match/confirm`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getWeclappContactsByParty(partyId: string) {
      return request<{ contacts: WeclappContactDetail[] }>(
        `/weclapp/parties/${encodeURIComponent(partyId)}/contacts`
      );
    },

    /** Wen fragen wir bei offenen Punkten? Melder + Haupt-Ansprechpartner + alle Kontakte. */
    async getTicketFollowUpContacts(ticketId: string) {
      return request<TicketFollowUpContacts>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/follow-up-contacts`
      );
    },

    async createWeclappContact(partyId: string, input: WeclappContactInput) {
      return request<{ contact: WeclappContactDetail }>(
        `/weclapp/parties/${encodeURIComponent(partyId)}/contacts`,
        { method: 'POST', body: JSON.stringify(input) }
      );
    },

    async updateWeclappContact(partyId: string, contactId: string, input: WeclappContactInput) {
      return request<{ contact: WeclappContactDetail }>(
        `/weclapp/parties/${encodeURIComponent(partyId)}/contacts/${encodeURIComponent(contactId)}`,
        { method: 'PUT', body: JSON.stringify(input) }
      );
    },

    getWeclappAttachmentDownloadUrl(ticketId: string, docId: string): string {
      return `${API_BASE}/weclapp/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(docId)}/download`;
    },

    /** Anhang in Office for the web öffnen (Scan-Gate + Upload in die Preview-Site). */
    async openAttachmentOfficeView(ticketId: string, docId: string) {
      return request<{ webUrl: string }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(docId)}/office-view`,
        { method: 'POST', body: '{}' }
      );
    },

    /** Virenscan-Verdikte (ClamAV + VirusTotal-Hash) für Anhänge; plant Unbekanntes automatisch ein. */
    async getAttachmentScanStatus(items: Array<{ docId: string; fileName?: string | null; ticketId?: string | null }>) {
      return request<{ statuses: Record<string, AttachmentScanStatus> }>(
        '/weclapp/attachments/scan-status',
        { method: 'POST', body: JSON.stringify({ items }) }
      );
    },
  };
}

export interface AttachmentScanStatus {
  /** PENDING | CLEAN | INFECTED | ERROR | TOO_LARGE | SKIPPED */
  clamavStatus: string;
  clamavSignature: string | null;
  /** PENDING | CLEAN | MALICIOUS | SUSPICIOUS | UNKNOWN | ERROR | SKIPPED */
  vtStatus: string;
  vtPositives: number | null;
  vtTotal: number | null;
  blocked: boolean;
}
